// Web Push subscription client. The service worker (public/service-worker.js) and manifest are
// Phase 0 of notifications-enrichment-plan.md -- deploy-gated and not validatable on docker compose.
// This util is the browser half of registration and is ready for that phase: once the SW file ships
// and VAPID keys are configured, `subscribeToPush` produces a PushSubscription and hands it to the
// registerPushSubscription mutation.

export const PUSH_SUPPORTED =
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

// iOS only allows Web Push from a home-screen-installed PWA, never a Safari tab.
export function isStandalone(): boolean {
  // navigator.standalone is iOS-only and not in the standard lib types.
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone;
  return window.matchMedia?.('(display-mode: standalone)').matches || iosStandalone === true;
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

// iOS wraps every browser in WebKit, so Chrome/Firefox/Edge on iOS all carry "Safari" in the UA
// too -- they're distinguishable only by their own browser token, which real Safari never carries.
// Push (and the install prompt) only work from actual Safari.
export function isIosSafari(): boolean {
  return isIos() && !/CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(navigator.userAgent);
}

// WebKit doesn't expose the OS version via any API -- iOS Safari embeds it in the UA string as
// "OS 16_4", so this is a string scrape, not a feature check.
export function getIosVersion(): [number, number] | null {
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  if (!match) return null;
  return [parseInt(match[1], 10), parseInt(match[2], 10)];
}

// Web Push on iOS Safari shipped in 16.4. Unknown/unparseable UA doesn't block -- let the real
// subscribe attempt fail with its own error rather than guessing wrong from a bad regex match.
export function iosVersionSupportsPush(): boolean {
  const version = getIosVersion();
  if (!version) return true;
  const [major, minor] = version;
  return major > 16 || (major === 16 && minor >= 4);
}

export type IosOnboardingState =
  | 'not-ios'
  | 'unsupported-version'
  | 'needs-safari'
  | 'needs-install'
  | 'needs-permission'
  | 'ready';

// Drives the guided iOS onboarding card in NotificationSettings. Order matters: version and browser
// gate whether install is even possible, before we can ask whether it's installed or permitted.
export function getIosOnboardingState(): IosOnboardingState {
  if (!isIos()) return 'not-ios';
  if (!iosVersionSupportsPush()) return 'unsupported-version';
  if (!isStandalone()) return isIosSafari() ? 'needs-install' : 'needs-safari';
  if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') return 'needs-permission';
  return 'ready';
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(char => char.charCodeAt(0)));
}

export interface PushSubscriptionPayload {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  userAgent: string;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  // Served from the site root so its scope is '/', per the deploy note (§5.6).
  return navigator.serviceWorker.register('/service-worker.js');
}

// Request permission (must be called from a user gesture on iOS) and subscribe. Returns the payload
// to send to registerPushSubscription, or throws with a user-presentable message.
export async function subscribeToPush(vapidPublicKey: string): Promise<PushSubscriptionPayload> {
  if (!PUSH_SUPPORTED) throw new Error('Push notifications are not supported in this browser.');
  if (isIos() && !isStandalone()) {
    throw new Error(
      'On iOS, add this app to your Home Screen first, then enable notifications from the installed app.',
    );
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was not granted.');

  const registration = await getRegistration();
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true, // iOS revokes the subscription if a push arrives and nothing is shown.
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('The browser returned an incomplete push subscription.');
  }
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    userAgent: navigator.userAgent,
  };
}

// Local display test (§5.4 Test 1): fire a notification straight from the page, no server. Proves the
// SW is registered and can render; isolates *display* from *delivery*. Self-sufficient: it requests
// permission if needed (showNotification silently rejects without it) and waits for the SW to become
// ACTIVE (register() resolves before activation, and showNotification needs an active worker).
export async function fireLocalTestNotification(): Promise<void> {
  if (!PUSH_SUPPORTED) throw new Error('Push notifications are not supported in this browser.');
  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission was not granted (check the browser and your OS notification settings).');
    }
  }
  await getRegistration();
  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification('Notifications are Enabled', {
    body: 'This device can display notifications.',
  });
}

export async function getCurrentEndpoint(): Promise<string | null> {
  if (!PUSH_SUPPORTED) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = await registration?.pushManager.getSubscription();
  return subscription?.endpoint ?? null;
}
